import React, { useState } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import {
  useLazyGetDesignationSalaryPreviewQuery,
  useBulkUpdateDesignationSalaryMutation,
} from "../../employees/employeesApi";

const MySwal = withReactContent(Swal);

// Designation Salary Bulk Update (Batch51, 2026-08-14) - REST port of the
// legacy Frm_General_Settings.cs "Update Salary" panel. Overwrites the
// Amount on EVERY Employee_Sallery row for every active employee holding the
// selected designation in the selected branch - no undo, no audit log
// (see Working_Files scratchpad handover doc). The preview call is the fix
// for the legacy version's blind confirmation dialog - it must always be
// shown before commit is allowed.
const BulkSalaryUpdateModal = ({ designations, initialDesignationId, onClose }) => {
  const { availableBranches } = useBranchFilter();

  const [branchId, setBranchId] = useState("");
  const [designationId, setDesignationId] = useState(
    initialDesignationId != null ? String(initialDesignationId) : ""
  );
  const [newAmount, setNewAmount] = useState("");
  const [preview, setPreview] = useState(null); // null = not yet previewed for the current selection

  const [fetchPreview, { isFetching: isPreviewing }] = useLazyGetDesignationSalaryPreviewQuery();
  const [commitBulkUpdate, { isLoading: isCommitting }] = useBulkUpdateDesignationSalaryMutation();

  const clearPreview = () => setPreview(null);

  const handleBranchChange = (e) => {
    setBranchId(e.target.value);
    clearPreview();
  };
  const handleDesignationChange = (e) => {
    setDesignationId(e.target.value);
    clearPreview();
  };

  const handlePreview = async () => {
    if (!branchId) {
      toast.error("Branch is required");
      return;
    }
    if (!designationId) {
      toast.error("Designation is required");
      return;
    }
    try {
      const result = await fetchPreview({
        branchId: Number(branchId),
        designationId: Number(designationId),
      }).unwrap();
      setPreview(result || []);
      if (!result || result.length === 0) {
        toast.error("No active employees with salary records hold this designation in this branch");
      }
    } catch (error) {
      toast.error(error?.message || "Could not load preview");
    }
  };

  const employeeCount = preview?.length || 0;

  const handleCommit = async () => {
    const amount = Number(newAmount);
    if (!newAmount || Number.isNaN(amount) || amount < 0) {
      toast.error("Enter a valid new salary amount");
      return;
    }
    const confirm = await MySwal.fire({
      title: "Update salary for every matching employee?",
      html: `This will update the salary for every active employee currently holding this
        designation in this branch (<strong>${employeeCount} employees</strong>).
        <strong>This cannot be undone.</strong> Make sure there are no pending
        salaries/claims/allowances to be paid.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, update salaries",
      cancelButtonText: "Cancel",
      customClass: { confirmButton: "btn btn-danger me-2", cancelButton: "btn btn-secondary" },
      buttonsStyling: false,
    });
    if (!confirm.isConfirmed) return;

    try {
      const result = await commitBulkUpdate({
        branchId: Number(branchId),
        designationId: Number(designationId),
        newAmount: amount,
      }).unwrap();
      toast.success(`${result.employeesUpdated} employee(s) updated.`);
      onClose();
    } catch (error) {
      toast.error(error?.message || "Could not update salaries");
    }
  };

  const canCommit = preview && preview.length > 0 && newAmount !== "" && !isCommitting;

  return (
    <Modal show onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Bulk Update Salary by Designation</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small">
          Overwrites the salary amount on every salary record for every active employee holding a
          designation, in one branch. This mirrors the legacy Update Salary tool - preview who's
          affected before committing, since there is no undo.
        </p>
        <div className="row">
          <div className="col-md-4 mb-3">
            <label className="form-label">Branch</label>
            <select className="form-control" value={branchId} onChange={handleBranchChange}>
              <option value="">--Select--</option>
              {availableBranches.map((b) => (
                <option key={b.branchId} value={b.branchId}>{b.branchCode}</option>
              ))}
            </select>
          </div>
          <div className="col-md-5 mb-3">
            <label className="form-label">Designation</label>
            <select className="form-control" value={designationId} onChange={handleDesignationChange}>
              <option value="">--Select--</option>
              {designations.map((d) => (
                <option key={d.designationCode} value={d.designationCode}>{d.designationName}</option>
              ))}
            </select>
          </div>
          <div className="col-md-3 mb-3">
            <label className="form-label">&nbsp;</label>
            <button
              type="button"
              className="btn btn-outline-primary d-block w-100"
              onClick={handlePreview}
              disabled={isPreviewing}
            >
              Preview
            </button>
          </div>
        </div>

        {preview !== null && (
          <>
            <div className="table-responsive mb-3" style={{ maxHeight: 260, overflowY: "auto" }}>
              <table className="table table-sm table-bordered mb-0">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Current Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-center text-muted">
                        No matching active employees with salary records
                      </td>
                    </tr>
                  ) : (
                    preview.map((r) => (
                      <tr key={r.recordGuid}>
                        <td>{r.empCode}</td>
                        <td>{r.empName} {r.empLastName}</td>
                        <td>{r.currentAmount}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {preview.length > 0 && (
              <div className="alert alert-warning py-2">
                {employeeCount} employee(s) will be updated.
              </div>
            )}
          </>
        )}

        <div className="row">
          <div className="col-md-5 mb-3">
            <label className="form-label">New Amount</label>
            <input
              type="number"
              className="form-control"
              value={newAmount}
              onChange={(e) => setNewAmount(e.target.value)}
              disabled={!preview || preview.length === 0}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button variant="danger" onClick={handleCommit} disabled={!canCommit}>
          Update Salaries
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BulkSalaryUpdateModal;
